import { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { Iconify } from '../iconify';
import { resolveActionLink } from './action-link';
import type { BannerItem, BannerSeverity } from './types';
import { useAutoDismiss } from './use-auto-dismiss';

const PALETTE = { info: 'info', success: 'success', warning: 'warning', critical: 'error' } as const;

const ICONS: Record<BannerSeverity, string> = {
  info: 'solar:info-circle-bold',
  success: 'solar:check-circle-bold',
  warning: 'solar:danger-triangle-bold',
  critical: 'solar:danger-bold',
};

export function NotificationCard({
  item,
  dismissLabel,
  onDismiss,
}: {
  item: BannerItem;
  dismissLabel: string;
  onDismiss: (id: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isAuto = item.dismiss === 'auto';
  const link = item.action ? resolveActionLink(item.action.url) : null;

  useAutoDismiss(isAuto ? item.autoDismissMs : undefined, hovered || focused, () => onDismiss(item.id));

  return (
    <Box
      data-testid="notification-card"
      data-notification-id={item.id}
      role={item.severity === 'info' || item.severity === 'success' ? 'status' : 'alert'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        pointerEvents: 'auto',
        color: 'text.primary',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.vars.palette.divider}`,
        borderLeft: `4px solid ${theme.vars.palette[PALETTE[item.severity]].main}`,
        borderRadius: 1,
        boxShadow: theme.vars.customShadows.dropdown,
      })}
    >
      <Iconify
        icon={ICONS[item.severity]}
        width={22}
        sx={(theme) => ({ flexShrink: 0, color: theme.vars.palette[PALETTE[item.severity]].light })}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
          {item.title}
        </Typography>
        {item.body && (
          <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
            {item.body}
          </Typography>
        )}
        {item.action && link && (
          <Button
            component="a"
            href={link.href}
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => onDismiss(item.id)}
            {...(link.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            sx={{ mt: 1, borderColor: 'divider' }}
          >
            {item.action.label}
          </Button>
        )}
      </Box>

      {!isAuto && (
        <IconButton
          size="small"
          aria-label={dismissLabel}
          onClick={() => onDismiss(item.id)}
          sx={{ flexShrink: 0, mt: -0.5, mr: -0.5, color: 'text.secondary' }}
        >
          <Iconify icon="solar:close-circle-bold" width={20} />
        </IconButton>
      )}
    </Box>
  );
}
